/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
  *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */

import { MusicTrace, TraceEvent } from "./trace";
import { GrowableAudioBuffer } from "./buffer";
import { WaveformData } from "./waveform";
import { Synthesizer } from "./synth";
import registerAudioWorklet from "audio-worklet:./worklets.js";


// progress function for recordIntoBuffer
export type AudioRenderProgress = (percent: number) => void;

/**
 * Use this interface to request an audio buffer render.
 */
export interface AudioRenderRequest {

    /// resource id for this request (e.g. gadget ID number)
    uuid : string;

    /// duration of the request (in beats)
    beats : number;

    /// start time of the request (in beats). defaults to zero
    start? : number;

    /// trace object containing the musical notes
    trace : MusicTrace;

    /// synth used to render audio
    synth : Synthesizer;

    /// force a re-render even if a cached buffer already exists
    force? : boolean;

    /// progress callback
    progress : (percent : number) => void;

    /// for internal use only to fulfill promise
    _callback? : (buffer : GrowableAudioBuffer) => void;
}


/**
 * This class maintains an asynchronous request queue to generate audio buffers.
 * This creates an abstraction layer around the synthesizer. 
 * By using a queue, it means we're only doing one offline audio context render at a time. 
 * This improves performance and makes loading much smoother.
 * One issue with OfflineAudioContext is that it can't be cancelled once a render starts.
 * This is a huge problem because audio rendering can take seconds or minutes to render
 * with complex synthesizers or effects.
 */
export class AudioLoadingService {

    /// queue for audio render requests
    private queue : AudioRenderRequest[] = [];

    /// cache of previously rendered audio
    private cache = new Map<string, GrowableAudioBuffer>();

    /// singleton pattern
    private static instance? : AudioLoadingService;


    private constructor() {
        setTimeout(() => this._processReqest(), 100);
    }

    private static init() : AudioLoadingService {
        if (!AudioLoadingService.instance) {
            AudioLoadingService.instance = new AudioLoadingService();
        }
        return AudioLoadingService.instance!;
    }

    /**
     * Requests offline audio render of the given resource.
     */
    public static requestAudioBuffer(request : AudioRenderRequest) : Promise<GrowableAudioBuffer> {
        const service = AudioLoadingService.init();
        return new Promise<GrowableAudioBuffer>((success) => {
            const cached = service.cache.get(request.uuid);
            const force = request.force === true;
            if (cached && !force) {
                success(cached);
            } else {
                request._callback = success;
                service.queue.push(request);
            }
        });
    }

    
    public static cancelRequest(uuid : string) {
        const service = AudioLoadingService.init();
        service.queue = service.queue.filter(r => r.uuid !== uuid);
    }


    public static clearCache() {
        AudioLoadingService.instance?.cache.clear();
    }


    public static clearCacheEntry(uuid : string) {
        AudioLoadingService.instance?.cache.delete(uuid);
    }


    private async _processReqest() {
        // unpack first request on the queue
        const r = this.queue.shift();
        if (r) {

            const force = (r.force === true);

            // if the audio object has already been rendered, simply return it.
            // clients should use clearCache or clearCacheEntry or the force property of a request 
            // to produce a fresh render.
            const cached = this.cache.get(r.uuid);
            if (!force && cached && r._callback) {
                r._callback(cached);
            }

            // render an empty audio buffer?
            else if (r.beats <= 0) {
                const gab = new GrowableAudioBuffer();
                this.cache.set(r.uuid, gab);
                if (r._callback) r._callback(gab);
            }

            // render full buffer
            else {
                const gab = await this.recordIntoBuffer(r);
                this.cache.set(r.uuid, gab);
                if (r._callback) r._callback(gab);
            }
        }
        setTimeout(() => this._processReqest(), 100);
    }


    /**
     * Render audio into a buffer using an offline audio context.
     * Beats must be greater than zero.
     */
    private async recordIntoBuffer(request : AudioRenderRequest) : Promise<GrowableAudioBuffer> {
        return new Promise(async (success) => {
            const start = request.start ? request.start : 0;
            const duration = request.beats * (60 / request.synth.bpm);
            const rate = 44100;
            const channels = 2;
            const frames = Math.round(rate * duration);
        
            // holds the resulting recording
            const gab = new GrowableAudioBuffer(channels, rate);

            // create offline audio context (stereo @ 44.1k sample rate)
            const oac = new OfflineAudioContext(channels, frames, rate);

            // audio worklet builds incremental waveform and reports on progress
            await registerAudioWorklet(oac);
            //await oac.audioWorklet.addModule('/assets/js/AudioWorklets.js');
            const recorder = new AudioWorkletNode(oac, "progress-monitor");
            let last_progress = 0.0;

            // as audio renders, our reporter node will send periodic updates
            recorder.port.onmessage = (e) => {
                const complete = e.data[2] / frames;
                if (complete > last_progress + 0.05) {
                    request.progress(Math.min(1.0, complete));
                    last_progress = complete;
                }
            }
            recorder.connect(oac.destination);

            // record into the context
            request.synth.scheduleNotes(request.trace, recorder, -start);

            const recording = await oac.startRendering();
            gab.appendAudioBuffer(recording);
            request.progress(1.0);
            success(gab);
        });
    }
}




/**
 * Use this interface to request visual waveform render
 */
export interface WaveformRenderRequest {

    /// resource id for this request (e.g. gadget ID number)
    uuid : string;

    /// duration of the request (in beats)
    beats : number;

    /// start time of the request (in beats). defaults to zero
    start? : number;

    /// how much of the waveform to render at a time (e.g. 4 beats)
    increment : number;

    /// trace object containing the musical notes
    trace : MusicTrace;

    /// synth to generate audio
    synth : Synthesizer;

    /// force a re-render even if a cached waveform already exists
    force? : boolean;

    /// progress callback invoked for each increment rendered
    progress : () => void;

    /// waveform being rendered
    waveform? : WaveformData;

    /// for internal use only to fulfill promise
    _callback? : (waveform : WaveformData) => void;
}


/**
 * This class maintains an asynchronous request queue to generate visual waveforms.
 * This creates an abstraction layer around the synthesizer. 
 * By using a queue, it means we're only doing one offline audio context render at a time. 
 * This improves performance and makes loading much smoother.
 * One issue with OfflineAudioContext is that it can't be cancelled once a render starts.
 * This is a huge problem because audio rendering can take seconds or minutes to render
 * with complex synthesizers or effects. Allows for chunking audio into smaller parts
 * if the goal is to create a waveform visualization.
 */
export class WaveformLoadingService {

    /// queue for audio render requests
    private queue = new Array<WaveformRenderRequest>();

    /// cache of previously rendered audio
    private cache = new Map<string, WaveformData>();

    /// singleton pattern
    private static instance? : WaveformLoadingService;


    private constructor() {
        setTimeout(() => this._processReqest(), 100);
    }

    private static init() : WaveformLoadingService {
        if (!WaveformLoadingService.instance) {
            WaveformLoadingService.instance = new WaveformLoadingService();
        }
        return WaveformLoadingService.instance!;
    }

    /**
     * Requests offline waveform render of the given resource.
     */
    public static requestWaveform(request : WaveformRenderRequest) : Promise<WaveformData> {
        const service = WaveformLoadingService.init();
        return new Promise<WaveformData>((success) => {
            const cached = service.cache.get(request.uuid);
            const force = (request.force === true);
            if (cached && !force) {
                success(cached);
            } else {
                request._callback = success;
                service.queue.push(request);
            }
        });
    }

    
    public static cancelRequest(uuid : string) {
        const service = WaveformLoadingService.init();
        service.queue = service.queue.filter(r => r.uuid !== uuid);
    }


    public static clearCache() {
        WaveformLoadingService.instance?.cache.clear();
    }


    public static clearCacheEntry(uuid : string) {
        WaveformLoadingService.instance?.cache.delete(uuid);
    }


    private async _processReqest() {
        // look at the first request on the queue
        const r = this.queue.shift();

        if (r) {
            const force = (r.force === true);
            const start = r.start ? r.start : 0;

            // if the audio object has already been rendered, we can return it.
            const cached = this.cache.get(r.uuid);
            if (!force && cached && r._callback) {
                r._callback(cached);
            }
            else {

                if (!r.waveform) {
                    r.waveform = new WaveformData();
                }

                // render the next segment
                if (r.beats > 0) {
                    await this.renderNextSegment(r);
                    r.progress();
                    r.start = start + r.increment;
                    r.beats -= r.increment;
                }

                // finished rendering?
                if (r.beats <= 0) {
                    this.cache.set(r.uuid, r.waveform);
                    if (r._callback) r._callback(r.waveform);
                }
                // still needs work, so add to the end of the queue
                else {
                    this.queue.push(r);
                }
            }
        }
        setTimeout(() => this._processReqest(), 100);
    }


    /**
     * Render audio into a buffer using an offline audio context.
     * Beats must be greater than zero.
     */
    private async renderNextSegment(request : WaveformRenderRequest) : Promise<void> {
        return new Promise(async (success) => {
            const start = request.start ? request.start : 0;
            const duration = request.increment * (60 / request.synth.bpm);
            const rate = 44100;
            const channels = 2;
            const frames = Math.round(rate * duration);

            // create offline audio context (stereo @ 44.1k sample rate)
            const oac = new OfflineAudioContext(channels, frames, rate);

            // audio worklet builds incremental waveform and reports on progress
            await registerAudioWorklet(oac);
            //await oac.audioWorklet.addModule('/assets/js/AudioWorklets.js');
            const recorder = new AudioWorkletNode(oac, "progress-monitor");

            // as audio renders, our reporter node will send periodic updates
            recorder.port.onmessage = (e) => {
                request.waveform?.appendSample(e.data);
            }
            recorder.connect(oac.destination);

            // record into the context
            request.synth.scheduleNotes(request.trace, recorder, -start);

            await oac.startRendering();
            success();
        });
    }
}
